<template>
    <v-container fluid class="pa-4">
        <!-- Header -->
        <v-row>
            <v-col cols="12">
                <v-card rounded="xl" elevation="4" class="mx-4 mt-4 mb-4 px-4 py-3"
                  style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);">
                  <div class="d-flex align-center">
                    <v-btn icon variant="text" color="white" size="small" @click="goBack">
                      <v-icon size="20">mdi-arrow-left</v-icon>
                    </v-btn>
                    <div class="ml-3 text-h6 font-weight-semibold" style="color: #ffffff;">
                      {{ $t('menu.analytics.title') }}
                    </div>
                    <v-spacer />
                    <img :src="logo" class="toolbar-icon-white" />
                  </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- Filters Section -->
        <v-card class="mb-6 filters-card" elevation="2">
            <v-card-text class="filters-content">
                <h3 class="mb-4 filters-title">{{ $t('menu.analytics.filters-label') }}</h3>
                <v-row>
                    <v-col cols="12" sm="6" md="4" lg="2" class="filter-col">
                        <div class="text-caption text-grey-darken-1 mb-1">{{ $t('menu.analytics.room-name-label') }}
                        </div>
                        <v-select v-model="filters.roomName" :items="roomOptions" variant="outlined" density="compact"
                            hide-details
                            :disabled="selectedKPI === 'all' || selectedKPI === 'attendance' || selectedKPI === 'colleagues'"></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="4" lg="2" class="filter-col">
                        <div class="text-caption text-grey-darken-1 mb-1">{{ $t('menu.analytics.room-category-label') }}
                        </div>
                        <v-select v-model="filters.category" :items="categoryOptions" variant="outlined"
                            density="compact" hide-details
                            :disabled="selectedKPI === 'all' || selectedKPI === 'attendance' || selectedKPI === 'colleagues'"></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="4" lg="2" class="filter-col">
                        <div class="text-caption text-grey-darken-1 mb-1">{{ $t('menu.analytics.floor-label') }}</div>
                        <v-select v-model="filters.floor" :items="floorOptions" variant="outlined" density="compact"
                            hide-details
                            :disabled="selectedKPI === 'all' || selectedKPI === 'attendance' || selectedKPI === 'colleagues'"></v-select>
                    </v-col>

                    <v-col cols="12" sm="6" md="4" lg="2" class="filter-col">
                        <div class="text-caption text-grey-darken-1 mb-1">{{ $t('menu.analytics.start-date') }}</div>
                        <v-text-field v-model="filters.startDate" type="date" variant="outlined" density="compact"
                            hide-details :disabled="selectedKPI === 'all'"></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="6" md="4" lg="2" class="filter-col">
                        <div class="text-caption text-grey-darken-1 mb-1">{{ $t('menu.analytics.end-date') }}</div>
                        <v-text-field v-model="filters.endDate" type="date" variant="outlined" density="compact"
                            hide-details :disabled="selectedKPI === 'all'"></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="6" md="4" lg="2" class="filter-col">
                        <div class="text-caption text-grey-darken-1 mb-1 d-none d-sm-block">&nbsp;</div>
                        <div class="filter-buttons">
                            <v-btn variant="text" @click="resetFilters" :disabled="selectedKPI === 'all'" size="small"
                                class="filter-btn reset-btn">
                                <v-icon start>mdi-refresh</v-icon>
                                <span class="d-none d-sm-inline">{{ $t('menu.analytics.reset-button') }}</span>
                            </v-btn>
                            <v-btn color="indigo-lighten-1" @click="applyFilters" rounded="lg"
                                :disabled="selectedKPI === 'all'" size="small" class="filter-btn apply-btn">
                                <v-icon start>mdi-filter</v-icon>
                                <span class="d-none d-sm-inline">{{ $t('menu.analytics.apply-button') }}</span>
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Analytics Dashboard Section -->
        <v-card elevation="2" class="analytics-section">
            <v-card-text>
                <h3 class="mb-4 kpi-title">{{ $t('menu.analytics.kpi-title') }}</h3>

                <!-- KPI Navigation -->
                <v-row>
                    <v-col cols="12" sm="4" md="3" lg="2" class="kpi-nav-col">
                        <v-list density="compact" class="pa-0 mb-6 kpi-list">
                            <v-list-item v-for="kpi in kpiOptions" :key="kpi.value" :active="selectedKPI === kpi.value"
                                @click="selectedKPI = kpi.value" :prepend-icon="kpi.icon" :title="kpi.label"
                                color="primary" class="mb-1 kpi-item" rounded="lg" />
                        </v-list>
                    </v-col>

                    <v-col cols="12" sm="8" md="9" lg="10" class="kpi-content-col">
                        <div class="kpi-content" :class="{ 'kpi-content-active': selectedKPI !== null }">
                            <!-- All KPIs View -->
                            <div v-if="selectedKPI === 'all'">
                                <!-- Top Stats Cards -->
                                <v-row class="mb-6 stats-row">
                                    <v-col cols="12" sm="6" md="3" class="stat-col">
                                        <v-card class="stat-card text-center" elevation="0" color="grey-lighten-5">
                                            <div class="stat-card-content">
                                                <div class="stat-header">
                                                    <div class="stat-info">
                                                        <div class="stat-label">{{
                                                            $t('menu.analytics.kpi-avg-occupancy') }}</div>
                                                        <div class="stat-value">75%</div>
                                                    </div>
                                                    <div class="stat-icon-wrapper">
                                                        <v-icon size="24" color="primary">mdi-trending-up</v-icon>
                                                    </div>
                                                </div>
                                                <div class="stat-trend text-success">
                                                    <v-icon size="small" color="success">mdi-arrow-up</v-icon>
                                                    <span>{{ $t('menu.analytics.kpi-avg-occupancy-value') }}</span>
                                                </div>
                                            </div>
                                        </v-card>
                                    </v-col>

                                    <v-col cols="12" sm="6" md="3" class="stat-col">
                                        <v-card class="stat-card text-center" elevation="0" color="grey-lighten-5">
                                            <div class="stat-card-content">
                                                <div class="stat-header">
                                                    <div class="stat-info">
                                                        <div class="stat-label">{{ $t('menu.analytics.available-now') }}
                                                        </div>
                                                        <div class="stat-value">{{
                                                            $t('menu.analytics.available-now-value') }}</div>
                                                    </div>
                                                    <div class="stat-icon-wrapper">
                                                        <v-icon size="24" color="success">mdi-desk</v-icon>
                                                    </div>
                                                </div>
                                                <div class="stat-trend text-success">
                                                    <v-icon size="small" color="success">mdi-arrow-up</v-icon>
                                                    <span>{{ $t('menu.analytics.available-now-change') }}</span>
                                                </div>
                                            </div>
                                        </v-card>
                                    </v-col>

                                    <v-col cols="12" sm="6" md="3" class="stat-col">
                                        <v-card class="stat-card text-center" elevation="0" color="grey-lighten-5">
                                            <div class="stat-card-content">
                                                <div class="stat-header">
                                                    <div class="stat-info">
                                                        <div class="stat-label">{{
                                                            $t('menu.analytics.working-days-this-month') }}</div>
                                                        <div class="stat-value">{{
                                                            $t('menu.analytics.working-days-this-month-value') }}</div>
                                                    </div>
                                                    <div class="stat-icon-wrapper">
                                                        <v-icon size="24" color="orange">mdi-calendar-month</v-icon>
                                                    </div>
                                                </div>
                                                <div class="stat-trend">&nbsp;</div>
                                            </div>
                                        </v-card>
                                    </v-col>

                                    <v-col cols="12" sm="6" md="3" class="stat-col">
                                        <v-card class="stat-card text-center" elevation="0"
                                            style="background-color: #5280d1">
                                            <div class="stat-card-content">
                                                <div class="stat-header">
                                                    <div class="stat-info">
                                                        <div class="stat-label" style="color: white">{{
                                                            $t('menu.analytics.peak-today') }}</div>
                                                        <div class="stat-value" style="color: white">{{
                                                            $t('menu.analytics.peak-today-value') }}</div>
                                                    </div>
                                                    <div class="stat-icon-wrapper white-icon">
                                                        <v-icon size="24">mdi-chart-line-variant</v-icon>
                                                    </div>
                                                </div>
                                                <div class="stat-trend">
                                                    <v-icon size="small" style="color: white">mdi-arrow-up</v-icon>
                                                    <span style="color: white">{{ $t('menu.analytics.peak-today-change')
                                                        }}</span>
                                                </div>
                                            </div>
                                        </v-card>
                                    </v-col>
                                </v-row>

                                <!-- Charts Row -->
                                <v-row class="mb-3 charts-row">
                                    <v-col cols="12" lg="8" class="chart-col">
                                        <v-card class="pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                            <h4 class="mb-4 chart-title">{{ $t('menu.analytics.weekly-occupancy-trends')
                                                }}</h4>
                                            <div class="chart-wrapper">
                                                <apexchart type="line" :height="chartHeight"
                                                    :options="weeklyOccupancyOptions" :series="weeklyOccupancySeries" />
                                            </div>
                                            <p class="utilization-text text-success font-weight-bold">{{
                                                $t('menu.analytics.weekly-utilization') }}</p>
                                        </v-card>
                                    </v-col>
                                    <v-col cols="12" lg="4" class="chart-col">
                                        <v-card class="pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                            <h4 class="mb-4 chart-title">{{ $t('menu.analytics.vacancy-duration') }}
                                            </h4>
                                            <p class="text-caption text-grey-darken-1 mb-4 chart-description">
                                                {{ $t('menu.analytics.vacancy-description') }}
                                            </p>
                                            <div class="chart-wrapper">
                                                <apexchart type="bar" :height="vacancyChartHeight"
                                                    :options="vacancyDurationOptions" :series="vacancyDurationSeries" />
                                            </div>
                                            <div class="text-center">
                                                <p class="text-caption text-grey">{{
                                                    $t('menu.analytics.average-downtime') }}</p>
                                            </div>
                                        </v-card>
                                    </v-col>
                                </v-row>

                                <!-- Bottom Row -->
                                <v-row class="bottom-row">
                                    <v-col cols="12" md="4" class="donut-col">
                                        <v-card class="pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                            <h4 class="mb-4 chart-title">{{ $t('menu.analytics.current-occupancy') }}
                                            </h4>
                                            <div class="d-flex justify-center donut-wrapper">
                                                <apexchart type="donut" :width="donutSize"
                                                    :options="currentOccupancyOptions"
                                                    :series="currentOccupancySeries" />
                                            </div>
                                            <v-row class="mt-4 legend-row">
                                                <v-col cols="6" class="text-center">
                                                    <div class="d-flex align-center justify-center">
                                                        <v-icon size="small" style="color: #11cd29"
                                                            class="mr-2">mdi-square</v-icon>
                                                        <span class="text-caption">{{ $t('menu.analytics.available')
                                                            }}</span>
                                                    </div>
                                                </v-col>
                                                <v-col cols="6" class="text-center">
                                                    <div class="d-flex align-center justify-center">
                                                        <v-icon size="small" style="color: #f0151e"
                                                            class="mr-2">mdi-square</v-icon>
                                                        <span class="text-caption">{{ $t('menu.analytics.occupied')
                                                            }}</span>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </v-card>
                                    </v-col>

                                    <v-col cols="12" md="8" class="calendar-col">
                                        <v-card class="pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                            <div class="calendar-header-section">
                                                <h4 class="calendar-title">{{ $t('menu.analytics.attendance-title') }}
                                                </h4>
                                                <div class="calendar-nav">
                                                    <v-btn icon size="small"
                                                        variant="text"><v-icon>mdi-chevron-left</v-icon></v-btn>
                                                    <span class="mx-2 font-weight-bold month-label">{{
                                                        $t('menu.analytics.attendance-month') }}</span>
                                                    <v-btn icon size="small"
                                                        variant="text"><v-icon>mdi-chevron-right</v-icon></v-btn>
                                                </div>
                                            </div>

                                            <v-row class="calendar-content">
                                                <v-col cols="12" lg="8" class="calendar-grid-col">
                                                    <div class="calendar-header mb-2 mt-2">
                                                        <div v-for="day in weekDays" :key="day"
                                                            class="calendar-header-day">{{ day }}</div>
                                                    </div>
                                                    <div class="calendar-grid">
                                                        <div v-for="day in calendarDays" :key="day.date"
                                                            class="calendar-day">
                                                            <div class="day-cell" :class="{
                                                                'office-day': day.type === 'office',
                                                                'remote-day': day.type === 'remote',
                                                                holiday: day.type === 'holiday',
                                                                absent: day.type === 'absent',
                                                            }">
                                                                {{ day.day }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </v-col>

                                                <v-col cols="12" lg="4" class="calendar-stats-col">
                                                    <div class="text-center calendar-stats">
                                                        <div class="mb-4">
                                                            <p class="text-caption text-grey-darken-1">
                                                                {{ $t('menu.analytics.attendance-analysis') }}
                                                            </p>
                                                        </div>

                                                        <div class="mb-3 legend-section">
                                                            <v-row dense>
                                                                <v-col cols="6">
                                                                    <div
                                                                        class="d-flex align-center justify-center mb-2">
                                                                        <div class="legend-box office-day"></div>
                                                                        <span class="text-caption ml-2">{{
                                                                            $t('menu.analytics.attendance-office-day')
                                                                            }}</span>
                                                                    </div>
                                                                </v-col>
                                                                <v-col cols="6">
                                                                    <div
                                                                        class="d-flex align-center justify-center mb-2">
                                                                        <div class="legend-box absent"></div>
                                                                        <span class="text-caption ml-2">{{
                                                                            $t('menu.analytics.attendance-absent')
                                                                            }}</span>
                                                                    </div>
                                                                </v-col>
                                                                <v-col cols="6">
                                                                    <div
                                                                        class="d-flex align-center justify-center mb-2">
                                                                        <div class="legend-box remote-day"></div>
                                                                        <span class="text-caption ml-1">{{
                                                                            $t('menu.analytics.attendance-remote-day')
                                                                            }}</span>
                                                                    </div>
                                                                </v-col>
                                                                <v-col cols="6">
                                                                    <div
                                                                        class="d-flex align-center justify-center mb-2">
                                                                        <div class="legend-box holiday"></div>
                                                                        <span class="text-caption ml-1">{{
                                                                            $t('menu.analytics.attendance-holiday')
                                                                            }}</span>
                                                                    </div>
                                                                </v-col>
                                                            </v-row>
                                                        </div>

                                                        <v-divider class="my-3"></v-divider>

                                                        <div class="stats-section">
                                                            <v-row dense>
                                                                <v-col cols="6">
                                                                    <div class="stat-number">13</div>
                                                                    <div class="stat-label-small">{{
                                                                        $t('menu.analytics.attendance-office-days') }}
                                                                    </div>
                                                                </v-col>
                                                                <v-col cols="6">
                                                                    <div class="stat-number">1</div>
                                                                    <div class="stat-label-small">{{
                                                                        $t('menu.analytics.attendance-absent-days') }}
                                                                    </div>
                                                                </v-col>
                                                            </v-row>
                                                            <v-row dense class="mt-2">
                                                                <v-col cols="6">
                                                                    <div class="stat-number">7</div>
                                                                    <div class="stat-label-small">{{
                                                                        $t('menu.analytics.attendance-remote-days') }}
                                                                    </div>
                                                                </v-col>
                                                                <v-col cols="6">
                                                                    <div class="stat-number text-success">90%</div>
                                                                    <div class="stat-label-small">{{
                                                                        $t('menu.analytics.attendance-rate') }}</div>
                                                                </v-col>
                                                            </v-row>
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Occupancy View -->
                            <div v-if="selectedKPI === 'occupancy'">
                                <div v-if="selectedKPI === 'occupancy'">
                                    <!-- Stats Cards -->
                                    <v-row class="mb-3">
                                        <v-col cols="12" sm="4">
                                            <v-card class="text-center pa-4 stat-card" elevation="0"
                                                color="grey-lighten-5">
                                                <div class="text-caption text-grey-darken-1 mb-1 font-weight-bold">
                                                    {{ $t('menu.analytics.available-now') }}
                                                </div>
                                                <div class="text-h4 font-weight-bold text-primary">{{
                                                    $t('menu.analytics.available-now-value') }}</div>
                                            </v-card>
                                        </v-col>

                                        <v-col cols="12" sm="4">
                                            <v-card class="text-center pa-4 stat-card" elevation="0"
                                                color="grey-lighten-5">
                                                <div class="text-caption text-grey-darken-1 mb-1 font-weight-bold">
                                                    {{ $t('menu.analytics.working-days-this-month') }}
                                                </div>
                                                <div class="text-h4 font-weight-bold text-warning">{{
                                                    $t('menu.analytics.working-days-this-month-value') }}</div>
                                            </v-card>
                                        </v-col>

                                        <v-col cols="12" sm="4">
                                            <v-card class="text-center pa-4 stat-card" elevation="0"
                                                color="grey-lighten-5">
                                                <div class="text-caption text-grey-darken-1 mb-1 font-weight-bold">
                                                    {{ $t('menu.analytics.total-desks') }}
                                                </div>
                                                <div class="text-h4 font-weight-bold">100</div>
                                            </v-card>
                                        </v-col>
                                    </v-row>

                                    <!-- Charts -->
                                    <v-row class="mb-3 charts-row">
                                        <v-col cols="12" lg="8" class="chart-col">
                                            <v-card class="pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                                <h4 class="mb-4 chart-title">{{
                                                    $t('menu.analytics.weekly-occupancy-trends') }}</h4>
                                                <div class="chart-wrapper">
                                                    <apexchart type="line" :height="chartHeight"
                                                        :options="weeklyOccupancyOptions"
                                                        :series="weeklyOccupancySeries" />
                                                </div>
                                                <p class="utilization-text text-success font-weight-bold">{{
                                                    $t('menu.analytics.weekly-utilization') }}</p>
                                            </v-card>
                                        </v-col>

                                        <v-col cols="12" lg="4" class="chart-col">
                                            <v-card class="pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                                <h4 class="mb-4 chart-title">{{ $t('menu.analytics.vacancy-duration') }}
                                                </h4>
                                                <p class="text-caption text-grey-darken-1 mb-4 chart-description">
                                                    {{ $t('menu.analytics.vacancy-description') }}
                                                </p>
                                                <div class="chart-wrapper">
                                                    <apexchart type="bar" :height="vacancyChartHeight"
                                                        :options="vacancyDurationOptions"
                                                        :series="vacancyDurationSeries" />
                                                </div>
                                                <div class="text-center">
                                                    <p class="text-caption text-grey">{{
                                                        $t('menu.analytics.average-downtime') }}</p>
                                                </div>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </div>
                            </div>

                            <!-- My Attendance View -->
                            <div v-if="selectedKPI === 'attendance'">
                                <v-row>
                                    <v-col cols="12" md="4">
                                        <!-- Stats Cards -->
                                        <v-card class="pa-4 mb-2 rounded-lg" elevation="0" color="grey-lighten-5">
                                            <div class="d-flex align-center justify-space-between mb-1">
                                                <span class="text-caption text-grey-darken-1 font-weight-bold">{{
                                                    $t('menu.analytics.avg-hours-per-day') }}</span>
                                                <div class="stat-icon-wrapper"><v-icon size="24"
                                                        color="primary">mdi-clock-outline</v-icon></div>
                                            </div>
                                            <div class="text-h4 font-weight-bold">8.1</div>
                                        </v-card>

                                        <v-card class="pa-4 mb-2 rounded-lg" elevation="0" color="grey-lighten-5">
                                            <div class="d-flex align-center justify-space-between mb-1">
                                                <span class="text-caption text-grey-darken-1 font-weight-bold">{{
                                                    $t('menu.analytics.working-days-this-month') }}</span>
                                                <div class="stat-icon-wrapper"><v-icon size="24"
                                                        color="orange">mdi-calendar-range</v-icon></div>
                                            </div>
                                            <div class="text-h4 font-weight-bold">{{
                                                $t('menu.analytics.working-days-this-month-value') }}</div>
                                        </v-card>

                                        <v-card class="pa-4 mb-2 rounded-lg" elevation="0" color="indigo">
                                            <div class="d-flex align-center justify-space-between mb-1">
                                                <span class="text-caption font-weight-bold">{{
                                                    $t('menu.analytics.days-absent-this-month') }}</span>
                                                <div class="stat-icon-wrapper white-icon"><v-icon
                                                        size="24">mdi-calendar-remove</v-icon></div>
                                            </div>
                                            <div class="text-h4 font-weight-bold">1</div>
                                        </v-card>

                                        <v-card class="pa-4 rounded-lg" elevation="0" color="indigo-darken-4">
                                            <div class="d-flex align-center justify-space-between mb-1">
                                                <span class="text-caption font-weight-bold">{{
                                                    $t('menu.analytics.attendance-rate') }}</span>
                                                <div class="stat-icon-wrapper white-icon"><v-icon
                                                        size="24">mdi-chart-line</v-icon></div>
                                            </div>
                                            <div class="text-h4 font-weight-bold">90%</div>
                                        </v-card>
                                    </v-col>

                                    <v-col cols="12" md="8">
                                        <v-card class="pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                            <div class="d-flex justify-space-between align-center mb-4">
                                                <h4>{{ $t('menu.analytics.attendance-title') }}</h4>
                                                <div class="d-flex align-center">
                                                    <v-btn icon size="small"
                                                        variant="text"><v-icon>mdi-chevron-left</v-icon></v-btn>
                                                    <span class="mx-2 font-weight-bold">{{
                                                        $t('menu.analytics.attendance-month') }}</span>
                                                    <v-btn icon size="small"
                                                        variant="text"><v-icon>mdi-chevron-right</v-icon></v-btn>
                                                </div>
                                            </div>

                                            <v-row>
                                                <v-col cols="12" lg="8">
                                                    <div class="calendar-header mb-2">
                                                        <div v-for="day in weekDays" :key="day"
                                                            class="calendar-header-day">{{ day }}</div>
                                                    </div>
                                                    <div class="calendar-grid">
                                                        <div v-for="(day, index) in calendarDaysAttendance" :key="index"
                                                            class="calendar-day">
                                                            <div v-if="day.day" class="day-cell" :class="{
                                                                'office-day': day.type === 'office',
                                                                'remote-day': day.type === 'remote',
                                                                holiday: day.type === 'holiday',
                                                                absent: day.type === 'absent',
                                                            }">
                                                                {{ day.day }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </v-col>

                                                <v-col cols="12" lg="4">
                                                    <div class="text-center">
                                                        <p class="text-caption text-grey-darken-1 mb-4">{{
                                                            $t('menu.analytics.attendance-analysis') }}</p>

                                                        <v-row dense class="mb-3">
                                                            <v-col cols="6">
                                                                <div class="d-flex align-center justify-center mb-2">
                                                                    <div class="legend-box office-day"></div>
                                                                    <span class="text-caption ml-2">{{
                                                                        $t('menu.analytics.attendance-office-day')
                                                                        }}</span>
                                                                </div>
                                                            </v-col>
                                                            <v-col cols="6">
                                                                <div class="d-flex align-center justify-center mb-2">
                                                                    <div class="legend-box absent"></div>
                                                                    <span class="text-caption ml-2">{{
                                                                        $t('menu.analytics.attendance-absent') }}</span>
                                                                </div>
                                                            </v-col>
                                                            <v-col cols="6">
                                                                <div class="d-flex align-center justify-center mb-2">
                                                                    <div class="legend-box remote-day"></div>
                                                                    <span class="text-caption ml-1">{{
                                                                        $t('menu.analytics.attendance-remote-day')
                                                                        }}</span>
                                                                </div>
                                                            </v-col>
                                                            <v-col cols="6">
                                                                <div class="d-flex align-center justify-center mb-2">
                                                                    <div class="legend-box holiday"></div>
                                                                    <span class="text-caption ml-1">{{
                                                                        $t('menu.analytics.attendance-holiday')
                                                                        }}</span>
                                                                </div>
                                                            </v-col>
                                                        </v-row>

                                                        <v-divider class="mb-3"></v-divider>

                                                        <v-row dense>
                                                            <v-col cols="6">
                                                                <div class="text-h6">13</div>
                                                                <div class="text-caption">{{
                                                                    $t('menu.analytics.attendance-office-days') }}</div>
                                                            </v-col>
                                                            <v-col cols="6">
                                                                <div class="text-h6">1</div>
                                                                <div class="text-caption">{{
                                                                    $t('menu.analytics.attendance-absent-days') }}</div>
                                                            </v-col>
                                                        </v-row>
                                                        <v-row dense class="mt-2">
                                                            <v-col cols="6">
                                                                <div class="text-h6">7</div>
                                                                <div class="text-caption">{{
                                                                    $t('menu.analytics.attendance-remote-days') }}</div>
                                                            </v-col>
                                                            <v-col cols="6">
                                                                <div class="text-h6 text-success">82%</div>
                                                                <div class="text-caption">{{
                                                                    $t('menu.analytics.attendance-rate') }}</div>
                                                            </v-col>
                                                        </v-row>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Colleagues Present View -->
                            <div v-if="selectedKPI === 'colleagues'">
                                <v-row class="mb-6">
                                    <v-col cols="12" sm="4">
                                        <v-card class="text-center pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                            <div class="text-caption text-grey-darken-1 mb-1 font-weight-bold">{{
                                                $t('menu.analytics.currently-present') }}</div>
                                            <div class="text-h3 font-weight-bold text-primary">58</div>
                                        </v-card>
                                    </v-col>
                                    <v-col cols="12" sm="4">
                                        <v-card class="text-center pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                            <div class="text-caption text-grey-darken-1 mb-1 font-weight-bold">{{
                                                $t('menu.analytics.peak-today') }}</div>
                                            <div class="text-h3 font-weight-bold text-orange">61</div>
                                        </v-card>
                                    </v-col>
                                    <v-col cols="12" sm="4">
                                        <v-card class="text-center pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                            <div class="text-caption text-grey-darken-1 mb-1 font-weight-bold">{{
                                                $t('menu.analytics.average-today') }}</div>
                                            <div class="text-h3 font-weight-bold">52</div>
                                        </v-card>
                                    </v-col>
                                </v-row>

                                <v-card class="pa-4 stat-card" elevation="0" color="grey-lighten-5">
                                    <h4 class="mb-4">{{ $t('menu.analytics.colleagues-present-today') }}</h4>
                                    <v-row>
                                        <v-col v-for="colleague in colleaguesList" :key="colleague.name" cols="12"
                                            sm="6" md="4" lg="3">
                                            <v-card class="pa-3 stat-card" elevation="0">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="40" :image="colleague.avatar"></v-avatar>
                                                    <div class="ml-3">
                                                        <div class="font-weight-medium">{{ colleague.name }}</div>
                                                        <div class="text-caption text-grey">{{
                                                            roleLabel(colleague.roleKey) }}</div>
                                                    </div>
                                                    <v-spacer></v-spacer>
                                                    <v-chip size="small"
                                                        :color="colleague.statusKey === 'present' ? 'success' : 'grey'"
                                                        variant="tonal">
                                                        {{ statusLabel(colleague.statusKey) }}
                                                    </v-chip>
                                                </div>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </v-card>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { getToolbarLogoImage } from '@/assets/Branding/branding.js'
import { useI18n } from 'vue-i18n'
import VueApexCharts from 'vue3-apexcharts'
import moment from 'moment'

// expose $t for use in script + template
const { t: $t } = useI18n()

const logo = ref('')
const goBack = () => window.history.back()

// Component registration for <apexchart />
const apexchart = VueApexCharts

// Reactive window width
const windowWidth = ref(window.innerWidth)

// Dynamic chart sizes based on screen width
const chartHeight = computed(() => {
    if (windowWidth.value < 450) return 250
    if (windowWidth.value < 768) return 280
    return 320
})

const vacancyChartHeight = computed(() => {
    if (windowWidth.value < 450) return 200
    if (windowWidth.value < 768) return 220
    return 250
})

const donutSize = computed(() => {
    if (windowWidth.value < 355) return 250
    if (windowWidth.value < 450) return 280
    if (windowWidth.value < 768) return 320
    if (windowWidth.value < 968) return 350
    return 400
})

// Handle window resize
const handleResize = () => {
    windowWidth.value = window.innerWidth
}
onMounted(async () => {
  logo.value = await getToolbarLogoImage()
  window.addEventListener('resize', handleResize)
})
onUnmounted(() => window.removeEventListener('resize', handleResize))

// State
const selectedKPI = ref('all')

// Weekday labels from i18n
const weekDays = computed(() => [
    $t('menu.analytics.day-mon'),
    $t('menu.analytics.day-tue'),
    $t('menu.analytics.day-wed'),
    $t('menu.analytics.day-thu'),
    $t('menu.analytics.day-fri'),
    $t('menu.analytics.day-sat'),
    $t('menu.analytics.day-sun'),
])

// Filters
const filters = ref({
    roomName: $t('menu.analytics.room-name-all'),
    category: $t('menu.analytics.room-category-all'),
    floor: $t('menu.analytics.floor-all'),
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
    person: $t('menu.analytics.kpi-all'),
})

// Filter options (translated)
const roomOptions = computed(() => [
    $t('menu.analytics.room-name-all'),
    $t('menu.analytics.room-101'),
    $t('menu.analytics.room-102'),
    $t('menu.analytics.room-103'),
])

const categoryOptions = computed(() => [
    $t('menu.analytics.room-category-all'),
    $t('menu.analytics.meeting-room'),
    $t('menu.analytics.workspace'),
    $t('menu.analytics.lounge'),
])

const floorOptions = computed(() => [
    $t('menu.analytics.floor-all'),
    $t('menu.analytics.floor-1'),
    $t('menu.analytics.floor-2'),
    $t('menu.analytics.floor-3'),
])

// KPI options (translated)
const kpiOptions = computed(() => [
    { value: 'all', label: $t('menu.analytics.kpi-all'), icon: 'mdi-view-dashboard' },
    { value: 'occupancy', label: $t('menu.analytics.kpi-occupancy'), icon: 'mdi-chart-line' },
    { value: 'attendance', label: $t('menu.analytics.kpi-my-attendance'), icon: 'mdi-calendar-check' },
    { value: 'colleagues', label: $t('menu.analytics.kpi-colleagues-present'), icon: 'mdi-account-group' },
])

// Colleagues data (role & status from i18n)
const colleaguesList = ref([
    { name: 'Max Johnson', roleKey: 'ceo', statusKey: 'present', avatar: ' https://randomuser.me/api/portraits/men/21.jpg' },
    { name: 'Tobias Hoffmann', roleKey: 'manager', statusKey: 'present', avatar: 'https://randomuser.me/api/portraits/men/32.jpg' },
    { name: 'Mina Chen', roleKey: 'designer', statusKey: 'present', avatar: 'https://randomuser.me/api/portraits/women/44.jpg' },
    { name: 'Anna Schmidt', roleKey: 'hr', statusKey: 'remote', avatar: 'https://randomuser.me/api/portraits/women/55.jpg' },
    { name: 'Sebastian Koch', roleKey: 'developer', statusKey: 'present', avatar: 'https://randomuser.me/api/portraits/men/46.jpg' },
    { name: 'Lisa Anderson', roleKey: 'designer', statusKey: 'remote', avatar: 'https://randomuser.me/api/portraits/women/24.jpg' },
    { name: 'Felix Wagner', roleKey: 'analyst', statusKey: 'present', avatar: 'https://randomuser.me/api/portraits/men/20.jpg' },
    { name: 'Matthias Richter', roleKey: 'developer', statusKey: 'remote', avatar: 'https://randomuser.me/api/portraits/men/75.jpg' },
    { name: 'Sophia Martinez', roleKey: 'manager', statusKey: 'remote', avatar: 'https://randomuser.me/api/portraits/women/81.jpg' },
    { name: 'Zhang Wei', roleKey: 'developer', statusKey: 'present', avatar: 'https://randomuser.me/api/portraits/men/90.jpg' },
    { name: 'Jonas Müller', roleKey: 'hr', statusKey: 'present', avatar: 'https://randomuser.me/api/portraits/men/64.jpg' },
    { name: 'Hannah Keller', roleKey: 'sales', statusKey: 'remote', avatar: 'https://randomuser.me/api/portraits/women/3.jpg' },
    { name: 'Laura Schäfer', roleKey: 'analyst', statusKey: 'present', avatar: 'https://randomuser.me/api/portraits/women/68.jpg' },
    { name: 'Lukas Schneider', roleKey: 'developer', statusKey: 'remote', avatar: 'https://randomuser.me/api/portraits/men/78.jpg' },
    { name: 'Julian Braun', roleKey: 'sales', statusKey: 'present', avatar: 'https://randomuser.me/api/portraits/men/79.jpg' },

])

// Translation helpers
const roleLabel = key => $t(`menu.analytics.role-${key}`)
const statusLabel = key => $t(`menu.analytics.colleagues-${key}`)

// --- Chart configurations (i18n-aware) ---
const weeklyOccupancyOptions = computed(() => ({
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { curve: 'smooth', width: [3, 2] },
    colors: ['#1976D2', '#FFA726'],
    xaxis: { categories: weekDays.value },
    yaxis: { min: 0, max: 100 },
    legend: { position: 'bottom', horizontalAlign: 'center' },
    grid: { borderColor: '#f1f1f1' },
    responsive: [{ breakpoint: 450, options: { xaxis: { labels: { rotate: -45, rotateAlways: true } } } }],
}))

const weeklyOccupancySeries = computed(() => [
    { name: $t('menu.analytics.average-daily-occupancy'), data: [40, 60, 45, 50, 40] },
    { name: $t('menu.analytics.average-week-occupancy'), data: [65, 70, 50, 75, 60] },
])

const vacancyDurationOptions = computed(() => ({
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: '60%', borderRadius: 8, dataLabels: { position: 'top' } } },
    colors: ['#64B5F6'],
    xaxis: {
        categories: [$t('menu.analytics.month-jul'), $t('menu.analytics.month-aug'), $t('menu.analytics.month-sep'), $t('menu.analytics.month-oct')],
        labels: { style: { colors: '#90A4AE', fontSize: '11px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
    },
    yaxis: { show: false },
    grid: { show: false },
    dataLabels: { enabled: false },
}))

const vacancyDurationSeries = [{ name: 'Vacancy', data: [35, 28, 22, 15] }]

const currentOccupancyOptions = computed(() => ({
    chart: { type: 'donut' },
    labels: [$t('menu.analytics.available'), $t('menu.analytics.occupied')],
    colors: ['#11cd29', '#f0151e'],
    legend: { show: false },
    dataLabels: {
        enabled: true,
        formatter: val => Math.round(val) + '%',
    },
    plotOptions: {
        pie: {
            donut: {
                size: '70%',
                labels: {
                    show: true,
                    total: {
                        show: true,
                        label: $t('menu.analytics.total-desks'),
                        formatter: () => '100',
                    },
                },
            },
        },
    },
}))

const currentOccupancySeries = [25, 75]

// Calendar data (unchanged values)
const calendarDays = ref([
    { day: 1, type: 'office' },
    { day: 2, type: 'office' },
    { day: 3, type: 'office' },
    { day: 4, type: 'office' },
    { day: 5, type: 'office' },
    { day: 6, type: null },
    { day: 7, type: 'null' },
    { day: 8, type: 'office' },
    { day: 9, type: 'office' },
    { day: 10, type: 'office' },
    { day: 11, type: 'office' },
    { day: 12, type: 'remote' },
    { day: 13, type: null },
    { day: 14, type: null },
    { day: 15, type: 'absent' },
    { day: 16, type: 'remote' },
    { day: 17, type: 'remote' },
    { day: 18, type: 'remote' },
    { day: 19, type: 'holiday' },
    { day: 20, type: null },
    { day: 21, type: null },
    { day: 22, type: 'holiday' },
    { day: 23, type: 'remote' },
    { day: 24, type: 'office' },
    { day: 25, type: 'office' },
    { day: 26, type: 'remote' },
    { day: 27, type: null },
    { day: 28, type: null },
    { day: 29, type: 'remote' },
    { day: 30, type: 'office' },
    { day: 31, type: 'office' },
])

const calendarDaysAttendance = ref([
    { day: 1, type: 'office' },
    { day: 2, type: 'office' },
    { day: 3, type: 'office' },
    { day: 4, type: 'office' },
    { day: 5, type: 'office' },
    { day: 6, type: null },
    { day: 7, type: 'null' },
    { day: 8, type: 'office' },
    { day: 9, type: 'office' },
    { day: 10, type: 'office' },
    { day: 11, type: 'office' },
    { day: 12, type: 'remote' },
    { day: 13, type: null },
    { day: 14, type: null },
    { day: 15, type: 'absent' },
    { day: 16, type: 'remote' },
    { day: 17, type: 'remote' },
    { day: 18, type: 'remote' },
    { day: 19, type: 'holiday' },
    { day: 20, type: null },
    { day: 21, type: null },
    { day: 22, type: 'holiday' },
    { day: 23, type: 'remote' },
    { day: 24, type: 'office' },
    { day: 25, type: 'office' },
    { day: 26, type: 'remote' },
    { day: 27, type: null },
    { day: 28, type: null },
    { day: 29, type: 'remote' },
    { day: 30, type: 'office' },
    { day: 31, type: 'office' },
])
// Methods
const resetFilters = () => {
    filters.value = {
        roomName: $t('menu.analytics.room-name-all'),
        category: $t('menu.analytics.room-category-all'),
        floor: $t('menu.analytics.floor-all'),
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().endOf('month').format('YYYY-MM-DD'),
        person: $t('menu.analytics.kpi-all'),
    }
}
const applyFilters = () => {
    console.log('Applying filters:', filters.value)
}

// Watch for KPI changes to reset filters accordingly
watch(selectedKPI, newValue => {
    if (newValue === 'attendance' || newValue === 'colleagues' || newValue === 'all') {
        filters.value.roomName = $t('menu.analytics.room-name-all')
        filters.value.category = $t('menu.analytics.room-category-all')
        filters.value.floor = $t('menu.analytics.floor-all')
    }
})
</script>

<style scoped>
.toolbar-icon-white {
    height: 30px;
    filter: brightness(0) invert(1);
}

/* Base styles */
.analytics-section {
    min-height: 600px;
    border-radius: 12px;
}

.filters-card {
    border-radius: 12px;
}

.kpi-content {
    background-color: #f5f5f5;
    border-radius: 8px;
    padding: 24px;
    min-height: 500px;
}

.kpi-content-active {
    background-color: #e8f0fe;
}

/* Updated stat card styles to match the image */
.stat-card {
    transition: all 0.3s ease;
    border-radius: 12px;
    height: 100%;
}

.stat-card:hover {
    transform: translateY(-2px);
}

.stat-card-content {
    padding: 20px;
}

.stat-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
}

.stat-info {
    flex: 1;
    text-align: left;
}

.stat-label {
    font-size: 14px;
    color: #666;
    font-weight: 500;
    margin-bottom: 8px;
}

.stat-value {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.2;
    color: #000;
}

.stat-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background-color: rgba(0, 0, 0, 0.04);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.stat-icon-wrapper.white-icon {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
}

.stat-trend {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    margin-top: 4px;
}

/* Chart styles */
.chart-card {
    height: 100%;
}

.utilization-text {
    text-align: right;
    margin-top: 16px;
    font-size: 16px;
}

/* Calendar styles */
.calendar-header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
}

.calendar-nav {
    display: flex;
    align-items: center;
}

.calendar-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
}

.calendar-header-day {
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #666;
}

.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
}

.calendar-day {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
}

.day-cell {
    width: 100%;
    height: 100%;
    max-width: 40px;
    max-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.day-cell:hover {
    transform: scale(1.1);
}

.office-day {
    background-color: #e3f2fd;
    color: #1976d2;
}

.remote-day {
    background-color: #fff3e0;
    color: #f57c00;
}

.holiday {
    background-color: #1976d2;
    color: white;
}

.absent {
    background-color: #fce4ec;
    color: #c2185b;
}

.legend-box {
    width: 16px;
    height: 16px;
    border-radius: 4px;
}

.stat-number {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
}

.stat-label-small {
    font-size: 12px;
    color: #666;
    margin-top: 4px;
}

/* Filter styles */
.filter-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.filter-btn {
    flex: 1;
    min-width: 80px;
}

/* Responsive Media Queries */

/* Desktop Large - 1200px and up */
@media (min-width: 1200px) {
    .stat-value {
        font-size: 32px;
    }

    .stat-label {
        font-size: 14px;
    }

    .chart-title {
        font-size: 18px;
    }
}

/* Desktop - 968px to 1199px */
@media (max-width: 1199px) and (min-width: 968px) {
    .stat-value {
        font-size: 28px;
    }

    .stat-label {
        font-size: 13px;
    }

    .stat-icon-wrapper {
        width: 44px;
        height: 44px;
    }

    .kpi-content {
        padding: 20px;
    }

    .chart-title {
        font-size: 16px;
    }
}

/* Tablet - 768px to 967px */
@media (max-width: 967px) and (min-width: 768px) {
    .stat-value {
        font-size: 24px;
    }

    .stat-label {
        font-size: 12px;
    }

    .stat-icon-wrapper {
        width: 40px;
        height: 40px;
    }

    .stat-icon-wrapper v-icon {
        font-size: 20px !important;
    }

    .stat-card-content {
        padding: 16px;
    }

    .kpi-content {
        padding: 16px;
    }

    .chart-title {
        font-size: 16px;
    }

    .calendar-header-section {
        flex-direction: column;
        align-items: flex-start;
    }

    .utilization-text {
        font-size: 14px;
    }
}

/* Mobile Large - 450px to 767px */
@media (max-width: 767px) and (min-width: 450px) {
    .dashboard-title {
        font-size: 20px;
    }

    .filters-title,
    .kpi-title {
        font-size: 18px;
    }

    .stat-card {
        margin-bottom: 12px;
    }

    .stat-value {
        font-size: 22px;
    }

    .stat-label {
        font-size: 11px;
    }

    .stat-icon-wrapper {
        width: 36px;
        height: 36px;
    }

    .stat-icon-wrapper v-icon {
        font-size: 18px !important;
    }

    .stat-card-content {
        padding: 14px;
    }

    .stat-trend {
        font-size: 11px;
    }

    .kpi-content {
        padding: 12px;
        margin-top: 16px;
    }

    .kpi-nav-col {
        margin-bottom: 16px;
    }

    .chart-title {
        font-size: 14px;
    }

    .chart-description {
        font-size: 11px;
    }

    .calendar-grid {
        gap: 4px;
    }

    .day-cell {
        font-size: 12px;
        max-width: 35px;
        max-height: 35px;
    }

    .calendar-header-day {
        font-size: 11px;
    }

    .month-label {
        font-size: 14px;
    }

    .filter-buttons {
        flex-direction: column;
    }

    .filter-btn {
        width: 100%;
    }

    .utilization-text {
        font-size: 14px;
        text-align: center;
        margin-top: 12px;
    }
}

/* Mobile Medium - 355px to 449px */
@media (max-width: 449px) and (min-width: 355px) {
    .dashboard-title {
        font-size: 18px;
    }

    .filters-title,
    .kpi-title {
        font-size: 16px;
    }

    .stat-card {
        margin-bottom: 10px;
    }

    .stat-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .stat-value {
        font-size: 20px;
        margin-bottom: 8px;
    }

    .stat-label {
        font-size: 11px;
    }

    .stat-icon-wrapper {
        width: 32px;
        height: 32px;
        position: absolute;
        top: 12px;
        right: 12px;
    }

    .stat-icon-wrapper v-icon {
        font-size: 16px !important;
    }

    .stat-card-content {
        padding: 12px;
        position: relative;
    }

    .stat-trend {
        font-size: 10px;
    }

    .kpi-content {
        padding: 10px;
    }

    .kpi-list .v-list-item {
        padding: 8px 12px;
    }

    .kpi-list .v-list-item-title {
        font-size: 13px;
    }

    .chart-card {
        padding: 12px !important;
    }

    .chart-title {
        font-size: 13px;
        margin-bottom: 8px !important;
    }

    .chart-description {
        font-size: 10px;
        margin-bottom: 8px !important;
    }

    .calendar-grid {
        gap: 3px;
    }

    .day-cell {
        font-size: 11px;
        max-width: 30px;
        max-height: 30px;
        border-radius: 6px;
    }

    .calendar-header-day {
        font-size: 10px;
    }

    .month-label {
        font-size: 13px;
    }

    .calendar-stats p {
        font-size: 10px;
    }

    .stat-number {
        font-size: 20px;
    }

    .stat-label-small {
        font-size: 10px;
    }

    .legend-box {
        width: 12px;
        height: 12px;
    }

    .legend-section .text-caption {
        font-size: 10px;
    }

    .filter-col {
        margin-bottom: 8px;
    }

    .filters-content {
        padding: 16px !important;
    }
}

/* Mobile Small - Below 355px */
@media (max-width: 354px) {
    .dashboard-title {
        font-size: 16px;
    }

    .filters-title,
    .kpi-title {
        font-size: 14px;
    }

    .stat-card {
        margin-bottom: 8px;
    }

    .stat-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .stat-value {
        font-size: 18px;
        margin-bottom: 6px;
    }

    .stat-label {
        font-size: 10px;
    }

    .stat-icon-wrapper {
        width: 28px;
        height: 28px;
        position: absolute;
        top: 10px;
        right: 10px;
    }

    .stat-icon-wrapper v-icon {
        font-size: 14px !important;
    }

    .stat-card-content {
        padding: 10px;
        position: relative;
    }

    .stat-trend {
        font-size: 9px;
    }

    .stat-trend v-icon {
        font-size: 12px !important;
    }

    .kpi-content {
        padding: 8px;
    }

    .kpi-list .v-list-item {
        padding: 6px 10px;
        min-height: 36px;
    }

    .kpi-list .v-list-item-title {
        font-size: 12px;
    }

    .kpi-list v-icon {
        font-size: 18px !important;
    }

    .chart-card {
        padding: 10px !important;
    }

    .chart-title {
        font-size: 12px;
        margin-bottom: 6px !important;
    }

    .chart-description {
        font-size: 9px;
        margin-bottom: 6px !important;
    }

    .calendar-grid {
        gap: 2px;
    }

    .day-cell {
        font-size: 10px;
        max-width: 25px;
        max-height: 25px;
        border-radius: 4px;
    }

    .calendar-header-day {
        font-size: 9px;
    }

    .month-label {
        font-size: 12px;
    }

    .calendar-stats p {
        font-size: 9px;
    }

    .stat-number {
        font-size: 18px;
    }

    .stat-label-small {
        font-size: 9px;
    }

    .legend-box {
        width: 10px;
        height: 10px;
    }

    .legend-section .text-caption {
        font-size: 9px;
    }

    .filter-col {
        margin-bottom: 6px;
    }

    .filters-content {
        padding: 12px !important;
    }

    .filter-buttons {
        margin-top: 8px;
    }

    .calendar-nav v-btn {
        width: 28px;
        height: 28px;
    }

    .calendar-nav v-icon {
        font-size: 16px !important;
    }

    /* Hide some elements on very small screens */
    .chart-description {
        display: none;
    }

    .utilization-text {
        font-size: 12px;
        text-align: center;
        margin-top: 8px;
    }
}

/* Special adjustments for stat cards on mobile */
@media (max-width: 600px) {
    .stats-row .stat-col:not(:last-child) {
        margin-bottom: 12px;
    }

    .stat-card {
        width: 100%;
    }
}

/* Ensure proper spacing on all screen sizes */
.v-container {
    padding: 16px !important;
}

@media (max-width: 450px) {
    .v-container {
        padding: 12px !important;
    }
}

@media (max-width: 355px) {
    .v-container {
        padding: 8px !important;
    }
}
</style>
